import { createApi } from '@reduxjs/toolkit/query/react';

import { REACT_APP_BE_API_STATS_URL, type SsoUserGroup } from '#shared';

import {
  asQueryFnResult,
  createFetchWithAuthBaseQuery,
  fetchWithAuthHeaders,
} from '#shared/api';

import type {
  StatsAOGDRAircraft,
  StatsAOGDRSummary,
  StatsDowntimeAircraft,
  StatsDowntimeSummary,
  StatsFHAircraft,
  StatsFHSummary,
  StatsFRAircraft,
  StatsFRSummary,
  StatsMTBAAircraft,
  StatsMTBASummary,
  StatsMTTRAircraft,
  StatsMTTRSummary,
  StatsUptimeAircraft,
  StatsUptimeSummary,
} from '../../types';
import type { ManipulateType } from 'dayjs';

// Centralized mapping of backend stats API endpoints
const paths = {
  aircraft: 'api/stats/aircrafts',
  aogdr: 'api/stats/aogdr',
  downtime: 'api/stats/downtime',
  fh: 'api/stats/fh',
  fr: 'api/stats/fr',
  mtba: 'api/stats/mtba',
  mttr: 'api/stats/mttr',
  uptime: 'api/stats/uptime',
  grantedGroups: 'api/granted-groups',
} as const;

/**
 * Serializable args for parameterized stats endpoints; `dateTo` is converted
 * to an ISO string here so the cache key stays stable and the store's serializable-state invariant holds.
 */
export interface StatsQueryArgs {
  dateTo: string;
  fromValue: number;
  fromUnitType: ManipulateType;
  aircraftsIdList?: number[];
}

// Structural mirror of page-level IHistAircraft, avoiding an upward features ->
// pages import; named *Option to disambiguate from the StatsAircraft breakdown row.
export interface StatsAircraftOption {
  Registration: string;
  Id: number;
}

interface StatsBreakdown<TSummary, TAircraft> {
  perDate: Record<string, TSummary>;
  summary: TSummary;
  perAircraft: Record<string, TAircraft>;
}

const buildStatsQuery = ({
  dateTo,
  fromValue,
  fromUnitType,
  aircraftsIdList,
}: StatsQueryArgs) =>
  new URLSearchParams({
    dateTo,
    fromValue: `${fromValue}`,
    fromUnitType,
    aircrafts: (aircraftsIdList ?? []).toString(),
  }).toString();

const withQuery = (path: string, args: StatsQueryArgs) =>
  `${path}?${buildStatsQuery(args)}`;

export const statsApi = createApi({
  reducerPath: 'statsApi',
  baseQuery: createFetchWithAuthBaseQuery(REACT_APP_BE_API_STATS_URL),
  refetchOnReconnect: true,
  endpoints: builder => ({
    getStatsAircrafts: builder.query<StatsAircraftOption[], void>({
      query: () => paths.aircraft,
    }),
    getStatsDowntime: builder.query<
      StatsBreakdown<StatsDowntimeSummary, StatsDowntimeAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.downtime, args),
    }),
    getStatsFh: builder.query<
      StatsBreakdown<StatsFHSummary, StatsFHAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.fh, args),
    }),
    getStatsFr: builder.query<
      StatsBreakdown<StatsFRSummary, StatsFRAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.fr, args),
    }),
    getStatsMtba: builder.query<
      StatsBreakdown<StatsMTBASummary, StatsMTBAAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.mtba, args),
    }),
    getStatsMttr: builder.query<
      StatsBreakdown<StatsMTTRSummary, StatsMTTRAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.mttr, args),
    }),
    getStatsUptime: builder.query<
      StatsBreakdown<StatsUptimeSummary, StatsUptimeAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.uptime, args),
    }),
    getStatsAogdr: builder.query<
      StatsBreakdown<StatsAOGDRSummary, StatsAOGDRAircraft>,
      StatsQueryArgs
    >({
      query: args => withQuery(paths.aogdr, args),
    }),
    // Granted groups come back in a response header, not the JSON body, so this
    // bypasses the shared base query — and therefore its timeout/abort handling.
    getGrantedGroups: builder.query<SsoUserGroup[], void>({
      queryFn: () =>
        asQueryFnResult(async () => {
          const res = await fetchWithAuthHeaders(
            `${REACT_APP_BE_API_STATS_URL}${paths.grantedGroups}`,
            false,
          );
          const userGroups = res.headers.get('user-groups');
          // Unvalidated cast on external header data.
          // TODO: validate once schema unification (Phase 6) lands.
          return JSON.parse(userGroups ?? '[]') as SsoUserGroup[];
        }),
    }),
  }),
});

export const {
  useGetStatsAircraftsQuery,
  useGetStatsDowntimeQuery,
  useGetStatsFhQuery,
  useGetStatsFrQuery,
  useGetStatsMtbaQuery,
  useGetStatsMttrQuery,
  useGetStatsUptimeQuery,
  useGetStatsAogdrQuery,
  useGetGrantedGroupsQuery,
} = statsApi;
